import React, { useState, useEffect } from "react";
import { fetchTasks, updateTaskStatus } from "../services/apiService";
import Loader from "../components/Loader";

export default function ViewTasks({ isDarkMode, toggleDarkMode }) {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeams, setSelectedTeams] = useState(["All"]); 
  const [refreshKey, setRefreshKey] = useState(0);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const teamsList = [
    'All',
    'Management',
    'Tech',
    'Design',
  'PR-Content',
    'Media',
    'Support'
  ];

  const handleTeamToggle = (team) => {
    if (team === 'All') {
      setSelectedTeams(['All']);
    } else {
      setSelectedTeams(prevTeams => {
        let currentSelection = prevTeams.filter(t => t !== 'All');
        
        if (currentSelection.includes(team)) {
          const newSelection = currentSelection.filter(t => t !== team);
          return newSelection.length === 0 ? ['All'] : newSelection;
        } else {
          return [...currentSelection, team];
        }
      });
    }
  };
  
  const getFilterType = (teams) => {
    if (teams.includes('All')) return '';
    
    const apiFilterValues = teams.map(team => {
  if (team === 'PR-Content') return 'PR,Content'; 
      return team;
    }).join(',');
    
    return apiFilterValues;
  };

  useEffect(() => {
    const loadTasks = async () => {
      setIsLoading(true); 
      setError(null);    
      
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          setError("Authorization token missing. Please log in.");
          setIsLoading(false);
          setTasks([]);
          return; 
        }

        const filterValue = getFilterType(selectedTeams);
        
        // Use cached API service
        const result = await fetchTasks(searchQuery, filterValue);
        
        if (result.success) {
          setTasks(result.data || []); 
        } else {
          throw new Error(result.message || 'Failed to fetch tasks');
        }
      } catch (err) {
        console.error('Failed to fetch tasks:', err);
        setError(err.message || "An unknown error occurred while fetching tasks.");
        setTasks([]); 
      } finally {
        setIsLoading(false); 
      }
    };

    loadTasks();
    
  }, [searchQuery, selectedTeams, refreshKey]); 

  const handleStatusChange = async (taskId, newStatus) => {
    setOpenStatusDropdown(null);
    try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            alert('Authorization token missing. Cannot update status.');
            return;
        }
        
        // Use API service with automatic cache invalidation
        const result = await updateTaskStatus(taskId, newStatus);

        if (result.success) {
            console.log("✅ Task status updated successfully, cache invalidated:", result.message);
            setRefreshKey(prev => prev + 1); 
        } else {
            alert(result.message || 'Failed to update task status');
            console.error("Update error details:", result);
        }
    } catch (error) {
        console.error('Error updating task status:', error);
        alert('A network error occurred while updating status.');
    }
  };
  
  const getStatusColor = (status) => {
    const lowerStatus = status?.toLowerCase();
    const colors = {
      'pending': { bg: '#e0e7ff', text: '#6366f1', border: '#818cf8', icon: 'fa-clock' },
      'completed': { bg: '#d1fae5', text: '#059669', border: '#34d399', icon: 'fa-check-circle' },
      'missed': { bg: '#fee2e2', text: '#dc2626', border: '#f87171', icon: 'fa-times-circle' },
    };
    return colors[lowerStatus] || colors['pending'];
  };

  const statusOptions = ['pending', 'completed', 'missed']; 

  const getTeamColor = (team) => {
    const colors = {
      All: { bg: 'rgba(102, 126, 234, 0.18)', border: 'rgba(102, 126, 234, 0.45)', color: '#667eea' },
      Management: { bg: 'rgba(251, 191, 36, 0.2)', border: 'rgba(251, 191, 36, 0.5)', color: '#fbbf24' },
      Tech: { bg: 'rgba(59, 130, 246, 0.2)', border: 'rgba(59, 130, 246, 0.5)', color: '#3b82f6' },
      Design: { bg: 'rgba(168, 85, 247, 0.2)', border: 'rgba(168, 85, 247, 0.5)', color: '#a855f7' },
  'PR-Content': { bg: 'rgba(34, 197, 94, 0.2)', border: 'rgba(34, 197, 94, 0.5)', color: '#22c55e' },
      Media: { bg: 'rgba(239, 68, 68, 0.2)', border: 'rgba(239, 68, 68, 0.5)', color: '#ef4444' },
      Support: { bg: 'rgba(14, 165, 233, 0.2)', border: 'rgba(14, 165, 233, 0.5)', color: '#0ea5e9' }
    };
    return colors[team] || colors.Tech;
  };
  
  const getPriorityColor = (priority) => {
    if (isDarkMode) {
      return {
        Low: { bg: 'rgba(99, 102, 241, 0.2)', text: '#818cf8', border: 'rgba(129, 140, 248, 0.4)' },
        Medium: { bg: 'rgba(234, 179, 8, 0.2)', text: '#fde047', border: 'rgba(253, 224, 71, 0.4)' },
        High: { bg: 'rgba(234, 88, 12, 0.2)', text: '#fb923c', border: 'rgba(251, 146, 60, 0.4)' }
      }[priority] || { bg: 'rgba(234, 179, 8, 0.2)', text: '#fde047', border: 'rgba(253, 224, 71, 0.4)' };
    }
    return {
      Low: { bg: '#e0e7ff', text: '#6366f1', border: '#818cf8' },
      Medium: { bg: '#fef9c3', text: '#eab308', border: '#fde047' },
      High: { bg: '#ffedd5', text: '#ea580c', border: '#fdba74' }
    }[priority] || { bg: '#fef9c3', text: '#eab308', border: '#fde047' };
  };

  const themeBg = isDarkMode 
    ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
    : '#f8f9fa';
  const themeText = isDarkMode ? 'rgba(255, 255, 255, 0.9)' : '#212529';
  const themeCardBg = isDarkMode ? 'rgba(30, 41, 59, 0.7)' : '#ffffff';
  const themeBorder = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.125)';
  const themeInputBg = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff';
  const themeShadow = isDarkMode 
    ? '0 0.5rem 1rem rgba(0, 0, 0, 0.4)' 
    : '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
  
  const filteredTasks = tasks.filter(task => task.status?.toLowerCase() === 'pending');

  return (
    <div 
      className={isDarkMode ? '' : 'bg-light'}
      style={{
        minHeight: '100vh',
        padding: 0,
        background: themeBg,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        position: 'relative',
        transition: 'background 0.3s ease',
        width: '100%'
      }}
    >
      <div style={{ maxWidth: '100%', margin: '0 auto', width: '100%' }}>
        <div 
          className="task-search-header"
          style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: windowWidth <= 768 ? '0.75rem' : '1.25rem',
          marginBottom: windowWidth <= 768 ? '1.25rem' : '2rem',
          flexWrap: 'wrap',
          width: '100%',
          padding: windowWidth <= 768 ? '0.75rem' : '0.875rem',
          background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.5)',
          borderRadius: windowWidth <= 768 ? '0.875rem' : '1rem',
          backdropFilter: 'blur(10px)',
          border: isDarkMode ? `1px solid ${themeBorder}` : '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: isDarkMode 
            ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
            : '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s ease',
          flexDirection: windowWidth <= 768 ? 'column' : 'row'
        }}>
          <div 
            className={isDarkMode ? '' : 'shadow'}
            style={{
              background: themeInputBg,
              backdropFilter: isDarkMode ? 'blur(10px)' : 'none',
              borderRadius: windowWidth <= 768 ? '0.875rem' : '1rem',
              flex: windowWidth <= 768 ? '0 1 100%' : '1 1 auto',
              minWidth: windowWidth <= 480 ? '100%' : (windowWidth <= 768 ? '100%' : '250px'),
              maxWidth: '100%',
              width: windowWidth <= 768 ? '100%' : 'auto',
              border: isDarkMode ? `1px solid ${themeBorder}` : '1px solid #dee2e6',
              boxShadow: isDarkMode ? 'none' : themeShadow,
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              margin: 0,
              boxSizing: 'border-box',
              display: 'block'
            }}
            onMouseOver={(e) => {
              if (!isDarkMode) {
                e.currentTarget.style.boxShadow = '0 0.5rem 1.5rem rgba(13, 110, 253, 0.3)';
                e.currentTarget.style.borderColor = '#0d6efd';
              }
            }}
            onMouseOut={(e) => {
              if (!isDarkMode) {
                e.currentTarget.style.boxShadow = themeShadow;
                e.currentTarget.style.borderColor = '#dee2e6';
              }
            }}
          >
            <div style={{ 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}>
              <div 
                className="search-icon-wrapper"
                style={{
                  position: 'absolute',
                  left: windowWidth <= 480 ? '0.875rem' : (windowWidth <= 768 ? '1rem' : '1rem'),
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  width: windowWidth <= 480 ? '1.25rem' : (windowWidth <= 768 ? '1.25rem' : '1.25rem'),
                  height: windowWidth <= 480 ? '1.25rem' : (windowWidth <= 768 ? '1.25rem' : '1.25rem')
                }}
              >
                {!isDarkMode ? (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #e7f1ff 0%, #cfe2ff 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #b6d4fe',
                    boxShadow: '0 2px 6px rgba(13, 110, 253, 0.2)',
                    transition: 'all 0.3s ease'
                  }}>
                    <i className="fas fa-search" style={{
                      color: '#0d6efd',
                      fontSize: '0.625rem',
                      fontWeight: '600',
                      lineHeight: '1'
                    }}></i>
                  </div>
                ) : (
                  <i className="fas fa-search" style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: windowWidth <= 480 ? '0.875rem' : (windowWidth <= 768 ? '0.875rem' : '1rem'),
                    transition: 'all 0.3s ease',
                    lineHeight: '1'
                  }}></i>
                )}
              </div>
              
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by tasks..."
                className={isDarkMode ? '' : 'form-control'}
                style={{
                  width: '100%',
                  position: 'relative',
                  paddingLeft: windowWidth <= 480 
                    ? '3.5rem'
                    : windowWidth <= 768 
                    ? '3.75rem'
                    : '3.75rem',
                  paddingRight: windowWidth <= 768 ? '1rem' : '1.25rem',
                  paddingTop: windowWidth <= 768 ? '0.875rem' : '0.875rem',
                  paddingBottom: windowWidth <= 768 ? '0.875rem' : '0.875rem',
                  minHeight: windowWidth <= 768 ? '48px' : '48px',
                  background: isDarkMode ? 'transparent' : 'transparent',
                  backdropFilter: 'none',
                  border: 'none',
                  borderRadius: windowWidth <= 768 ? '0.875rem' : '1rem',
                  color: themeText,
                  fontSize: windowWidth <= 480 ? '16px' : (windowWidth <= 768 ? '16px' : '1rem'),
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  fontWeight: '400',
                  boxSizing: 'border-box',
                  textAlign: 'left',
                  lineHeight: '1.5'
                }}
                onFocus={(e) => {
                  if (!isDarkMode) {
                    e.target.parentElement.parentElement.style.boxShadow = '0 0.5rem 1.5rem rgba(13, 110, 253, 0.4)';
                    e.target.parentElement.parentElement.style.borderColor = '#0d6efd';
                  } else {
                    e.target.parentElement.querySelector('.fa-search').style.color = 'rgba(102, 126, 234, 0.9)';
                  }
                }}
                onBlur={(e) => {
                  if (!isDarkMode) {
                    e.target.parentElement.parentElement.style.boxShadow = themeShadow;
                    e.target.parentElement.parentElement.style.borderColor = '#dee2e6';
                  } else {
                    e.target.parentElement.querySelector('.fa-search').style.color = 'rgba(255, 255, 255, 0.6)';
                  }
                }}
              />
            </div>
          </div>

        </div>

        <div style={{ 
          marginBottom: '2.5rem',
          textAlign: 'center'
        }}>
          <h1 
            style={{
              fontSize: windowWidth <= 375 ? '1.375rem' : (windowWidth <= 480 ? '1.5rem' : (windowWidth <= 768 ? '2rem' : '2.75rem')),
              fontWeight: '800',
              textAlign: 'center',
              marginBottom: '0.5rem',
              marginTop: 0,
              letterSpacing: windowWidth <= 375 ? '0.03em' : '0.05em',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: windowWidth <= 375 ? '0.5rem' : (windowWidth <= 768 ? '0.75rem' : '1.25rem'),
              animation: 'fadeInDown 0.6s ease-out',
              flexWrap: 'wrap'
            }}
          >
            <div style={{
              width: windowWidth <= 375 ? '2.5rem' : (windowWidth <= 768 ? '3rem' : '4rem'),
              height: windowWidth <= 375 ? '2.5rem' : (windowWidth <= 768 ? '3rem' : '4rem'),
              borderRadius: windowWidth <= 375 ? '0.625rem' : (windowWidth <= 768 ? '0.75rem' : '1rem'),
              background: isDarkMode 
                ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.4) 0%, rgba(118, 75, 162, 0.4) 100%)'
                : 'linear-gradient(135deg, #0d6efd 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isDarkMode 
                ? '0 4px 15px rgba(102, 126, 234, 0.4)' 
                : '0 4px 15px rgba(13, 110, 253, 0.4)',
              border: isDarkMode ? '2px solid rgba(102, 126, 234, 0.6)' : '2px solid rgba(255, 255, 255, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
                animation: 'rotate 10s linear infinite'
              }}></div>
              <i className="fas fa-tasks" style={{ 
                fontSize: '2rem', 
                color: isDarkMode ? '#a5b4fc' : 'white',
                filter: isDarkMode ? 'drop-shadow(0 2px 4px rgba(102, 126, 234, 0.5))' : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                zIndex: 1,
                position: 'relative'
              }}></i>
            </div>
            <span style={{
              color: isDarkMode ? '#ffffff' : '#0d6efd',
              background: isDarkMode 
                ? 'none'
                : 'linear-gradient(135deg, #0d6efd 0%, #764ba2 100%)',
              WebkitBackgroundClip: isDarkMode ? 'unset' : 'text',
              WebkitTextFillColor: isDarkMode ? themeText : 'transparent',
              backgroundClip: isDarkMode ? 'unset' : 'text',
              textShadow: isDarkMode ? '0 2px 8px rgba(102, 126, 234, 0.5)' : 'none',
              fontWeight: '800'
            }}>
              Task Dashboard
            </span>
          </h1>
          <p style={{
            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#6c757d',
            fontSize: '1.125rem',
            marginTop: '0.5rem',
            fontWeight: '400',
            letterSpacing: '0.025em'
          }}>
            Manage and track all your team tasks
          </p>

          <div 
            className={isDarkMode ? '' : 'shadow'}
            style={{
              background: isDarkMode 
                ? 'rgba(30, 30, 30, 0.8)' 
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: windowWidth <= 768 ? '1rem' : '1.25rem',
              padding: windowWidth <= 768 ? '1.25rem' : '2rem',
              border: isDarkMode ? `1px solid ${themeBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: isDarkMode 
                ? '0 4px 20px rgba(0, 0, 0, 0.4)' 
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              marginTop: windowWidth <= 768 ? '1.5rem' : '2rem',
              transition: 'all 0.3s ease'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: windowWidth <= 768 ? '2rem' : '2.5rem',
                height: windowWidth <= 768 ? '2rem' : '2.5rem',
                borderRadius: windowWidth <= 768 ? '0.5rem' : '0.75rem',
                background: isDarkMode 
                  ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                  : 'linear-gradient(135deg, #0d6efd 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isDarkMode 
                  ? '0 2px 8px rgba(102, 126, 234, 0.3)' 
                  : '0 2px 8px rgba(13, 110, 253, 0.3)'
              }}>
                <i className="fas fa-filter" style={{
                  color: isDarkMode ? '#818cf8' : 'white',
                  fontSize: '1rem'
                }}></i>
              </div>
              <h3 style={{
                color: themeText,
                fontWeight: '700',
                marginBottom: 0,
                textAlign: 'center',
                fontSize: windowWidth <= 768 ? '1rem' : '1.25rem',
                background: isDarkMode 
                  ? 'none'
                  : 'linear-gradient(135deg, #0d6efd 0%, #764ba2 100%)',
                WebkitBackgroundClip: isDarkMode ? 'unset' : 'text',
                WebkitTextFillColor: isDarkMode ? themeText : 'transparent',
                backgroundClip: isDarkMode ? 'unset' : 'text',
                lineHeight: '1.2'
              }}>
                Filter by Team
              </h3>
            </div>
            <div
              className="team-filter-grid"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: windowWidth <= 375
                  ? '1.25rem'
                  : windowWidth <= 480
                  ? '1.5rem'
                  : windowWidth <= 768
                  ? '1.75rem'
                  : '2rem',
                padding: windowWidth <= 375
                  ? '1.5rem 0.75rem'
                  : windowWidth <= 480
                  ? '1.75rem 1rem'
                  : windowWidth <= 768
                  ? '2rem 1.25rem'
                  : '2.5rem 1.5rem',
                background: isDarkMode
                  ? 'rgba(255, 255, 255, 0.02)'
                  : 'rgba(102, 126, 234, 0.04)',
                borderRadius: '1rem',
                border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(102, 126, 234, 0.15)'}`,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              {teamsList.map((team) => {
                const isSelected = selectedTeams.includes(team);
                const teamColors = getTeamColor(team);
                return (
                  <div
                    key={team}
                    style={{
                      textAlign: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      minWidth: windowWidth <= 375
                        ? '5.5rem'
                        : windowWidth <= 480
                        ? '6rem'
                        : windowWidth <= 768
                        ? '6.75rem'
                        : '7.5rem',
                      maxWidth: windowWidth <= 375
                        ? '5.5rem'
                        : windowWidth <= 480
                        ? '6rem'
                        : windowWidth <= 768
                        ? '6.75rem'
                        : '7.5rem',
                      gap: windowWidth <= 375 ? '0.5rem' : '0.625rem',
                      flex: '0 0 auto'
                    }}
                    onClick={() => handleTeamToggle(team)}
                  >
                    <div
                      style={{
                        width: windowWidth <= 375
                          ? '3.25rem'
                          : windowWidth <= 480
                          ? '3.5rem'
                          : windowWidth <= 768
                          ? '3.9rem'
                          : '4.25rem',
                        height: windowWidth <= 375
                          ? '3.25rem'
                          : windowWidth <= 480
                          ? '3.5rem'
                          : windowWidth <= 768
                          ? '3.9rem'
                          : '4.25rem',
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: isSelected
                          ? `linear-gradient(135deg, ${teamColors.color} 0%, ${teamColors.color}dd 100%)`
                          : isDarkMode
                          ? 'rgba(255, 255, 255, 0.05)'
                          : themeCardBg,
                        border: isSelected
                          ? `${windowWidth <= 480 ? '2.5px' : '3px'} solid ${teamColors.border}`
                          : `${windowWidth <= 480 ? '1.75px' : '2.5px'} solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : themeBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isSelected
                          ? windowWidth <= 480
                            ? 'scale(1.05)'
                            : 'scale(1.15)'
                          : 'scale(1)',
                        boxShadow: isSelected
                          ? `0 6px 18px ${teamColors.color}33`
                          : isDarkMode
                          ? 'none'
                          : '0 2px 8px rgba(0, 0, 0, 0.08)',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                      onMouseOver={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'scale(1.12) translateY(-4px)';
                          e.currentTarget.style.background = `linear-gradient(135deg, ${teamColors.color} 0%, ${teamColors.color}dd 100%)`;
                          e.currentTarget.style.borderColor = teamColors.border;
                          e.currentTarget.style.boxShadow = `0 8px 20px ${teamColors.color}33`;
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.background = isDarkMode
                            ? 'rgba(255, 255, 255, 0.05)'
                            : themeCardBg;
                          e.currentTarget.style.borderColor = isDarkMode
                            ? 'rgba(255, 255, 255, 0.1)'
                            : themeBorder;
                          e.currentTarget.style.boxShadow = isDarkMode
                            ? 'none'
                            : '0 2px 8px rgba(0, 0, 0, 0.08)';
                        }
                      }}
                    >
                      {isSelected && windowWidth > 480 && (
                        <div
                          style={{
                            position: 'absolute',
                            width: '120%',
                            height: '120%',
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${teamColors.color}25 0%, transparent 70%)`,
                            animation: 'pulse 2s ease-in-out infinite',
                            pointerEvents: 'none',
                            zIndex: 0
                          }}
                        ></div>
                      )}
                      <i
                        className={`fas ${
                          team === 'All'
                            ? 'fa-layer-group'
                            : team === 'Management'
                            ? 'fa-crown'
                            : team === 'Tech'
                            ? 'fa-microchip'
                            : team === 'Design'
                            ? 'fa-palette'
                            : team === 'PR-Content'
                            ? 'fa-bullhorn'
                            : team === 'Media'
                            ? 'fa-video'
                            : team === 'Support'
                            ? 'fa-headset'
                            : 'fa-users'
                        }`}
                        style={{
                          fontSize: windowWidth <= 375
                            ? '1.35rem'
                            : windowWidth <= 480
                            ? '1.45rem'
                            : windowWidth <= 768
                            ? '1.55rem'
                            : '1.75rem',
                          color: isSelected ? '#ffffff' : teamColors.color,
                          position: 'relative',
                          zIndex: 1,
                          transition: 'all 0.3s ease'
                        }}
                      ></i>
                    </div>
                    <p
                      style={{
                        color: isSelected
                          ? teamColors.color
                          : isDarkMode
                          ? 'rgba(255, 255, 255, 0.85)'
                          : '#495057',
                        fontSize: windowWidth <= 375
                          ? '0.75rem'
                          : windowWidth <= 480
                          ? '0.8125rem'
                          : '0.875rem',
                        marginTop: windowWidth <= 375 ? '0.35rem' : '0.5rem',
                        marginBottom: 0,
                        fontWeight: isSelected ? 600 : 500,
                        textAlign: 'center',
                        lineHeight: '1.2',
                        whiteSpace: windowWidth <= 480 ? 'normal' : 'nowrap',
                        padding: windowWidth <= 375 ? '0 0.25rem' : 0,
                        minHeight: windowWidth <= 375 ? '2rem' : 'auto'
                      }}
                    >
                      {team}
                    </p>
                    {isSelected && windowWidth > 480 && (
                      <div
                        style={{
                          width: '70%',
                          height: '2px',
                          background: `linear-gradient(90deg, transparent 0%, ${teamColors.color} 50%, transparent 100%)`,
                          marginTop: '0.35rem',
                          borderRadius: '2px',
                          animation: 'slideIn 0.3s ease-out'
                        }}
                      ></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: windowWidth <= 768 ? '1.25rem' : '1.5rem',
          padding: windowWidth <= 480 ? '0' : '0 0.5rem',
          flexWrap: windowWidth <= 480 ? 'wrap' : 'nowrap',
          gap: windowWidth <= 480 ? '0.75rem' : '0'
        }}>
          <h3 style={{
            color: themeText,
            fontSize: windowWidth <= 480 ? '1rem' : (windowWidth <= 768 ? '1.125rem' : '1.25rem'),
            fontWeight: '700',
            margin: 0
          }}>
            Tasks ({filteredTasks.length})
          </h3>
        </div>

        {isLoading && tasks.length === 0 && (
          <Loader variant="page" text="Fetching the task list..." isDarkMode={isDarkMode} size="lg" />
        )}

        {isLoading && tasks.length > 0 && (
          <div style={{ textAlign: 'center', padding: '0.75rem', marginBottom: '1rem', background: isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.1)', borderRadius: '0.75rem', border: `1px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}` }}>
            <Loader variant="inline" size="sm" text="Updating task list..." isDarkMode={isDarkMode} color="#667eea" />
          </div>
        )}

        {error && !isLoading && (
            <div 
              style={{ 
                textAlign: 'center', 
                padding: '4rem', 
                color: '#dc2626', 
                background: isDarkMode ? 'rgba(220, 38, 38, 0.1)' : '#fee2e2', 
                borderRadius: '1rem', 
                border: '1px solid #f87171',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
              }}
            >
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '3rem', marginBottom: '1rem', color: '#dc2626' }}></i>
                <p style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Error Loading Tasks</p>
                <p style={{ fontSize: '1rem' }}>{error}</p>
                <button
                    onClick={() => setRefreshKey(prev => prev + 1)}
                    style={{
                        marginTop: '1rem',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.5rem',
                        border: 'none',
                        background: '#dc2626',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: '600'
                    }}
                >
                    Try Again
                </button>
            </div>
        )}

        {(!isLoading || tasks.length > 0) && !error && (
            <div 
                className="tasks-grid"
                style={{
                display: 'flex',
                flexDirection: 'column',
                gap: windowWidth <= 480 ? '1rem' : (windowWidth <= 768 ? '1.25rem' : '1.5rem'),
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                alignItems: 'stretch'
            }}>
            {filteredTasks.length > 0 ? (
                filteredTasks.map((task, index) => {
                const priorityColor = getPriorityColor(task.priority);
                const isMobile = windowWidth <= 768;
                const isTablet = windowWidth > 768 && windowWidth <= 1200;
                const shouldStackAssignees = isMobile || isTablet;
                const assignedTeams = task.assigned_team || [];
                const assignedMembers = task.assignedTo || [];
                const infoGridColumns = isMobile
                  ? '1fr'
                  : 'minmax(240px, 1.5fr) minmax(180px, 1fr) minmax(150px, auto) minmax(150px, auto)';
                const infoGridGap = isMobile ? '1.25rem' : '1.75rem';

                return (
                  <div
                    key={task._id || index}
                    className={isDarkMode ? 'card-3d glass-card-dark' : 'card-3d glass-card'}
                    style={{
                      borderRadius: windowWidth <= 768 ? '0.875rem' : '1.25rem',
                      padding: windowWidth <= 768 ? '1.25rem' : '1.5rem',
                      border: isDarkMode
                        ? `2px solid rgba(102, 126, 234, 0.3)`
                        : `2px solid rgba(102, 126, 234, 0.2)`,
                      cursor: 'default',
                      overflow: 'visible',
                      position: 'relative',
                      zIndex: openStatusDropdown === task._id ? 9999 : 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: windowWidth <= 768 ? '1.25rem' : '1.5rem',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      background: isDarkMode
                        ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
                      boxShadow: isDarkMode
                        ? '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                        : '0 4px 20px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
                    }}
                    onMouseMove={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px) translateX(4px)';
                      e.currentTarget.style.boxShadow = isDarkMode
                        ? '0 12px 40px rgba(102, 126, 234, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                        : '0 12px 40px rgba(102, 126, 234, 0.3), inset 0 1px 0 rgba(255, 255, 255, 1)';
                      e.currentTarget.style.borderColor = isDarkMode
                        ? 'rgba(102, 126, 234, 0.6)'
                        : 'rgba(102, 126, 234, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) translateX(0)';
                      e.currentTarget.style.boxShadow = isDarkMode
                        ? '0 4px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                        : '0 4px 20px rgba(102, 126, 234, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                      e.currentTarget.style.borderColor = isDarkMode
                        ? 'rgba(102, 126, 234, 0.3)'
                        : 'rgba(102, 126, 234, 0.2)';
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: windowWidth <= 768 ? '0.75rem' : '1.25rem'
                      }}
                    >
                      <div
                        style={{
                          minWidth: windowWidth <= 768 ? '3rem' : '3.5rem',
                          height: windowWidth <= 768 ? '3rem' : '3.5rem',
                          borderRadius: windowWidth <= 768 ? '0.75rem' : '1rem',
                          background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: isDarkMode
                            ? '0 4px 15px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                            : '0 6px 20px rgba(102, 126, 234, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                          border: isDarkMode
                            ? '2px solid rgba(102, 126, 234, 0.5)'
                            : '2px solid rgba(255, 255, 255, 0.4)',
                          flexShrink: 0,
                          position: 'relative',
                          overflow: 'visible'
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: '-50%',
                            left: '-50%',
                            width: '200%',
                            height: '200%',
                            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
                            animation: 'rotate 10s linear infinite'
                          }}
                        ></div>
                        <i
                          className="fas fa-clipboard-list"
                          style={{
                            color: isDarkMode ? '#a5b4fc' : 'white',
                            fontSize: windowWidth <= 768 ? '1.25rem' : '1.5rem',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
                            zIndex: 1,
                            position: 'relative'
                          }}
                        ></i>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          minWidth: 0,
                          flex: 1
                        }}
                      >
                        <h3
                          className={isDarkMode ? '' : 'card-title'}
                          style={{
                            fontSize: windowWidth <= 768 ? '1.0625rem' : '1.25rem',
                            fontWeight: 800,
                            color: themeText,
                            margin: 0,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            lineHeight: 1.3,
                            background: isDarkMode
                              ? 'none'
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: isDarkMode ? 'unset' : 'text',
                            WebkitTextFillColor: isDarkMode ? themeText : 'transparent',
                            backgroundClip: isDarkMode ? 'unset' : 'text',
                            whiteSpace: 'normal'
                          }}
                        >
                          {task.name}
                        </h3>
                        {task.description && (
                          <p
                            style={{
                              color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : '#64748b',
                              fontSize: windowWidth <= 768 ? '0.8125rem' : '0.9375rem',
                              margin: 0,
                              lineHeight: 1.5,
                              wordBreak: 'break-word',
                              overflowWrap: 'anywhere',
                              fontWeight: 400,
                              whiteSpace: 'normal'
                            }}
                          >
                            {task.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: infoGridColumns,
                        gap: infoGridGap,
                        width: '100%',
                        alignItems: 'flex-start'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: shouldStackAssignees ? '0.5rem' : '0.6rem',
                          width: '100%'
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.55)' : '#64748b'
                          }}
                        >
                          Assigned Teams & Members
                        </span>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: shouldStackAssignees ? '0.45rem' : '0.6rem',
                            width: '100%'
                          }}
                        >
                          {assignedTeams.map((teamName, idx) => (
                            <span
                              key={`team-${idx}`}
                              className={isDarkMode ? '' : 'badge'}
                              style={{
                                padding: windowWidth <= 768 ? '0.375rem 0.75rem' : '0.5rem 1rem',
                                borderRadius: '0.75rem',
                                fontSize: windowWidth <= 768 ? '0.75rem' : '0.8125rem',
                                fontWeight: 700,
                                background: isDarkMode
                                  ? 'rgba(168, 85, 247, 0.3)'
                                  : 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                                color: 'white',
                                border: isDarkMode ? '1px solid rgba(168, 85, 247, 0.5)' : 'none',
                                boxShadow: isDarkMode ? 'none' : '0 3px 8px rgba(168, 85, 247, 0.3)',
                                display: 'grid',
                                gridTemplateColumns: 'auto minmax(0, 1fr)',
                                alignItems: 'center',
                                columnGap: windowWidth <= 768 ? '0.4rem' : '0.6rem',
                                rowGap: windowWidth <= 768 ? '0.2rem' : '0.25rem',
                                width: shouldStackAssignees ? '100%' : 'fit-content',
                                maxWidth: '100%'
                              }}
                            >
                              <div
                                style={{
                                  width: windowWidth <= 768 ? '1rem' : '1.25rem',
                                  height: windowWidth <= 768 ? '1rem' : '1.25rem',
                                  borderRadius: '50%',
                                  background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                <i
                                  className="fas fa-users"
                                  style={{
                                    fontSize: windowWidth <= 768 ? '0.5rem' : '0.625rem',
                                    color: 'white'
                                  }}
                                ></i>
                              </div>
                              <span
                                style={{
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  textTransform: 'capitalize',
                                  lineHeight: 1.3
                                }}
                              >
                                {teamName} Team
                              </span>
                            </span>
                          ))}

                          {assignedMembers.map((assignee, idx) => {
                            const name = assignee.fullname;
                            return (
                              <span
                                key={`member-${idx}`}
                                className={isDarkMode ? '' : 'badge bg-primary'}
                                style={{
                                  padding: windowWidth <= 768 ? '0.375rem 0.75rem' : '0.4rem 0.9rem',
                                  borderRadius: '0.75rem',
                                  fontSize: windowWidth <= 768 ? '0.6875rem' : '0.75rem',
                                  fontWeight: 600,
                                  background: isDarkMode
                                    ? 'rgba(102, 126, 234, 0.3)'
                                    : 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                                  color: 'white',
                                  border: isDarkMode ? '1px solid rgba(102, 126, 234, 0.5)' : 'none',
                                  boxShadow: isDarkMode ? 'none' : '0 3px 8px rgba(13, 110, 253, 0.3)',
                                  display: 'grid',
                                  gridTemplateColumns: 'auto minmax(0, 1fr)',
                                  alignItems: 'center',
                                  columnGap: windowWidth <= 768 ? '0.375rem' : '0.5rem',
                                  width: shouldStackAssignees ? '100%' : 'fit-content',
                                  maxWidth: '100%'
                                }}
                              >
                                <div
                                  style={{
                                    width: windowWidth <= 768 ? '1rem' : '1.25rem',
                                    height: windowWidth <= 768 ? '1rem' : '1.25rem',
                                    borderRadius: '50%',
                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: isDarkMode ? 'none' : 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                                    flexShrink: 0
                                  }}
                                >
                                  <i
                                    className="fas fa-user"
                                    style={{
                                      fontSize: windowWidth <= 768 ? '0.5rem' : '0.625rem',
                                      color: 'white'
                                    }}
                                  ></i>
                                </div>
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {name}
                                </span>
                              </span>
                            );
                          })}

                          {assignedTeams.length === 0 && assignedMembers.length === 0 && (
                            <span
                              style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '0.75rem',
                                fontSize: windowWidth <= 768 ? '0.75rem' : '0.8125rem',
                                fontWeight: 500,
                                background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f8f9fa',
                                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#6c757d'
                              }}
                            >
                              Unassigned
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.6rem'
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.55)' : '#64748b'
                          }}
                        >
                          Status
                        </span>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenStatusDropdown(openStatusDropdown === task._id ? null : task._id);
                          }}
                          style={{
                            padding: windowWidth <= 768 ? '0.625rem 1rem' : '0.75rem 1.25rem',
                            borderRadius: '0.625rem',
                            fontSize: windowWidth <= 768 ? '0.8125rem' : '0.875rem',
                            fontWeight: 600,
                            background: isDarkMode
                              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
                              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                            color: getStatusColor(task.status).text,
                            border: `2px solid ${getStatusColor(task.status).border}`,
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: isDarkMode
                              ? `0 4px 15px ${getStatusColor(task.status).text}20`
                              : `0 4px 15px ${getStatusColor(task.status).text}25`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.75rem',
                            transform: openStatusDropdown === task._id ? 'translateY(-2px)' : 'translateY(0)',
                            width: '100%'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = isDarkMode
                              ? `0 6px 20px ${getStatusColor(task.status).text}30`
                              : `0 6px 20px ${getStatusColor(task.status).text}35`;
                          }}
                          onMouseLeave={(e) => {
                            if (openStatusDropdown !== task._id) {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = isDarkMode
                                ? `0 4px 15px ${getStatusColor(task.status).text}20`
                                : `0 4px 15px ${getStatusColor(task.status).text}25`;
                            }
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.625rem',
                              flex: 1
                            }}
                          >
                            <div
                              style={{
                                width: '1.75rem',
                                height: '1.75rem',
                                borderRadius: '0.5rem',
                                background: `linear-gradient(135deg, ${getStatusColor(task.status).bg} 0%, ${getStatusColor(task.status).text}30 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: `0 2px 8px ${getStatusColor(task.status).text}20`
                              }}
                            >
                              <i
                                className={`fas ${getStatusColor(task.status).icon}`}
                                style={{
                                  fontSize: '0.875rem',
                                  color: getStatusColor(task.status).text
                                }}
                              ></i>
                            </div>
                            <span
                              style={{
                                fontWeight: 600,
                                letterSpacing: '0.3px',
                                textTransform: 'capitalize'
                              }}
                            >
                              {task.status}
                            </span>
                          </div>
                          <i
                            className={`fas fa-chevron-${openStatusDropdown === task._id ? 'up' : 'down'}`}
                            style={{
                              fontSize: '0.75rem',
                              transition: 'transform 0.3s ease',
                              transform: openStatusDropdown === task._id ? 'rotate(180deg)' : 'rotate(0deg)'
                            }}
                          ></i>
                        </div>

                        {openStatusDropdown === task._id && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 0.5rem)',
                              left: 0,
                              right: 0,
                              background: isDarkMode
                                ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.98) 0%, rgba(30, 30, 60, 0.98) 100%)'
                                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                              backdropFilter: 'blur(20px)',
                              border: `2px solid ${isDarkMode ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
                              borderRadius: '0.75rem',
                              boxShadow: isDarkMode
                                ? '0 15px 50px rgba(0, 0, 0, 0.5)'
                                : '0 15px 50px rgba(0, 0, 0, 0.15)',
                              zIndex: 10000,
                              overflow: 'hidden',
                              animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              minWidth: '100%'
                            }}
                          >
                            {statusOptions.map((status, idxOption) => {
                              const statusColors = getStatusColor(status);
                              const isSelected = task.status === status;
                              return (
                                <div
                                  key={status}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusChange(task._id, status);
                                  }}
                                  style={{
                                    padding: '1rem 1.25rem',
                                    cursor: 'pointer',
                                    background: isSelected
                                      ? isDarkMode
                                        ? `linear-gradient(90deg, ${statusColors.bg}40 0%, transparent 100%)`
                                        : `linear-gradient(90deg, ${statusColors.bg}20 0%, transparent 100%)`
                                      : 'transparent',
                                    borderLeft: isSelected
                                      ? `4px solid ${statusColors.text}`
                                      : '4px solid transparent',
                                    borderBottom:
                                      idxOption < statusOptions.length - 1
                                        ? `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'}`
                                        : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.95)' : '#2c3e50'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = isDarkMode
                                      ? `linear-gradient(90deg, ${statusColors.bg}30 0%, transparent 100%)`
                                      : `linear-gradient(90deg, ${statusColors.bg}15 0%, transparent 100%)`;
                                    e.currentTarget.style.paddingLeft = '1.5rem';
                                    e.currentTarget.style.borderLeft = `4px solid ${statusColors.text}`;
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.paddingLeft = '1.25rem';
                                    if (!isSelected) {
                                      e.currentTarget.style.background = 'transparent';
                                      e.currentTarget.style.borderLeft = '4px solid transparent';
                                    } else {
                                      e.currentTarget.style.background = isDarkMode
                                        ? `linear-gradient(90deg, ${statusColors.bg}40 0%, transparent 100%)`
                                        : `linear-gradient(90deg, ${statusColors.bg}20 0%, transparent 100%)`;
                                      e.currentTarget.style.borderLeft = `4px solid ${statusColors.text}`;
                                    }
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '2rem',
                                      height: '2rem',
                                      borderRadius: '0.5rem',
                                      background: `linear-gradient(135deg, ${statusColors.bg} 0%, ${statusColors.text}30 100%)`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: `0 2px 8px ${statusColors.text}20`
                                    }}
                                  >
                                    <i
                                      className={`fas ${statusColors.icon}`}
                                      style={{
                                        fontSize: '0.875rem',
                                        color: statusColors.text
                                      }}
                                    ></i>
                                  </div>
                                  <span
                                    style={{
                                      flex: 1,
                                      fontWeight: isSelected ? 700 : 500,
                                      fontSize: '0.875rem',
                                      letterSpacing: '0.3px',
                                      textTransform: 'capitalize'
                                    }}
                                  >
                                    {status}
                                  </span>
                                  {isSelected && (
                                    <div
                                      style={{
                                        width: '1.5rem',
                                        height: '1.5rem',
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${statusColors.text} 0%, ${statusColors.text}80 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 2px 8px ${statusColors.text}40`
                                      }}
                                    >
                                      <i
                                        className="fas fa-check"
                                        style={{
                                          fontSize: '0.625rem',
                                          color: '#ffffff'
                                        }}
                                      ></i>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.6rem'
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.55)' : '#64748b'
                          }}
                        >
                          Priority
                        </span>
                        <span
                          className={isDarkMode ? '' : 'badge'}
                          style={{
                            padding: windowWidth <= 768 ? '0.375rem 0.625rem' : '0.375rem 0.75rem',
                            borderRadius: '0.5rem',
                            fontSize: windowWidth <= 768 ? '0.6875rem' : '0.75rem',
                            fontWeight: 600,
                            background: isDarkMode
                              ? priorityColor.bg
                              : `linear-gradient(135deg, ${priorityColor.bg} 0%, ${priorityColor.text}20 100%)`,
                            color: priorityColor.text,
                            border: `1.5px solid ${priorityColor.border}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.375rem',
                            boxShadow: isDarkMode ? 'none' : `0 2px 6px ${priorityColor.text}25`,
                            whiteSpace: 'nowrap',
                            width: shouldStackAssignees ? 'fit-content' : 'auto'
                          }}
                        >
                          <div
                            style={{
                              width: windowWidth <= 768 ? '0.875rem' : '1rem',
                              height: windowWidth <= 768 ? '0.875rem' : '1rem',
                              borderRadius: '50%',
                              background: isDarkMode
                                ? 'rgba(255, 255, 255, 0.2)'
                                : `${priorityColor.text}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${priorityColor.text}40`,
                              flexShrink: 0
                            }}
                          >
                            <i
                              className={`fas ${
                                task.priority === 'High'
                                  ? 'fa-fire'
                                  : task.priority === 'Medium'
                                  ? 'fa-exclamation-circle'
                                  : 'fa-check-circle'
                              }`}
                              style={{
                                fontSize: windowWidth <= 768 ? '0.4375rem' : '0.5rem',
                                color: priorityColor.text
                              }}
                            ></i>
                          </div>
                          <span style={{ textTransform: 'capitalize' }}>{task.priority || 'Medium'}</span>
                        </span>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.6rem'
                        }}
                      >
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.55)' : '#64748b'
                          }}
                        >
                          Deadline
                        </span>
                        {task.deadline ? (
                          <span
                            className={isDarkMode ? '' : 'text-muted'}
                            style={{
                              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : '#6c757d',
                              fontSize: windowWidth <= 768 ? '0.8125rem' : '0.875rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {!isDarkMode && (
                              <div
                                style={{
                                  width: windowWidth <= 768 ? '1.25rem' : '1.5rem',
                                  height: windowWidth <= 768 ? '1.25rem' : '1.5rem',
                                  borderRadius: '0.375rem',
                                  background: 'linear-gradient(135deg, #e7f1ff 0%, #cfe2ff 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1px solid #b6d4fe',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                  flexShrink: 0
                                }}
                              >
                                <i
                                  className="fas fa-calendar-alt"
                                  style={{
                                    fontSize: windowWidth <= 768 ? '0.5625rem' : '0.625rem',
                                    color: '#0d6efd'
                                  }}
                                ></i>
                              </div>
                            )}
                            {isDarkMode && <i className="fas fa-calendar-alt" style={{ flexShrink: 0 }}></i>}
                            <span>{new Date(task.deadline).toLocaleDateString()}</span>
                          </span>
                        ) : (
                          <span
                            style={{
                              color: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : '#9ca3af',
                              fontSize: windowWidth <= 768 ? '0.8125rem' : '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            Not set
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
              ) : (
                <div 
                  className={isDarkMode ? '' : 'card shadow'}
                  style={{
                    textAlign: 'center',
                    padding: '4rem',
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#6c757d',
                    fontSize: '1.25rem',
                    background: isDarkMode ? 'transparent' : '#ffffff',
                    borderRadius: '1rem',
                    border: isDarkMode ? 'none' : '1px solid #dee2e6'
                  }}
                >
                  <i className="fas fa-tasks" style={{ 
                    fontSize: '3rem', 
                    marginBottom: '1rem', 
                    opacity: 0.5, 
                    display: 'block',
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : '#adb5bd'
                  }}></i>
                  No Pending tasks found
                </div>
              )}
            </div>
        )}
      </div>
    </div>
  );
}